// src/pages/AdminPage.js
import { useState, useEffect } from "react";
import { auth, provider } from "../firebase";
import {
  signInWithPopup,
  signInWithRedirect,
  signOut,
  onAuthStateChanged,
  getRedirectResult,
} from "firebase/auth";
import {
  collection,
  getDocs, deleteDoc
} from "firebase/firestore";
import { db } from "../firebase";
import { isMobile } from "react-device-detect";
import { doc, setDoc } from "firebase/firestore";

import AddOffer from "../components/AddOffer";
import AddBankOffer from "../components/AddBankOffer";
import AddWebsiteOffer from "../components/AddWebsiteOffer";
import AddWeeklyAds from "../components/AddWeeklyAds";
import Cache from "../utils/cache";
import AddVendor from "../components/AddVendor";
import UpdateOffer from "../components/UpdateOffer";
import AddSpecialOffer from "../components/AddSpecialOffer";

const ADMIN_EMAIL = "ymmvdeal@gmail.com";

export default function AdminPage() {

  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [view, setView] = useState("product");

  const [productOffers, setProductOffers] = useState([]);
  const [bankOffers, setBankOffers] = useState([]);
  const [websiteOffers, setWebsiteOffers] = useState([]);
  const [vendors, setVendors] = useState([]); // <-- vendors state

  const [loadingProduct, setLoadingProduct] = useState(false);
  const [loadingBank, setLoadingBank] = useState(false);
  const [loadingWebsite, setLoadingWebsite] = useState(false);
  const [loadingVendors, setLoadingVendors] = useState(false); // <-- loading for vendors

  const [deleteVendor, setDeleteVendor] = useState("");
  const [deleteOfferType, setDeleteOfferType] = useState("");
  const [deleteProductNames, setDeleteProductNames] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState("");

  const [deleteExpiredView, setDeleteExpiredView] = useState(""); // "product" or "bank"
  const [deleteExpiredDate, setDeleteExpiredDate] = useState("");
  const [deletingExpired, setDeletingExpired] = useState(false);
  const [deleteExpiredResult, setDeleteExpiredResult] = useState("");
  const [whatsAppText, setWhatsAppText] = useState("");
  const [vendorName, setVendorName] = useState("");

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result && result.user) {
          setUser(result.user);
          setChecking(false);
          return;
        }
      } catch (error) {
        console.error("Redirect login error:", error);
      }

      // Fallback to auth state listener
      const unsub = onAuthStateChanged(auth, (u) => {
        setUser(u);
        setChecking(false);
      });

      return () => unsub();
    };

    checkAuth();
  }, []);


  useEffect(() => {
    const cached = Cache.getData();
    if (
      !cached ||
      !cached.productOffers?.length ||
      !cached.bankOffers?.length ||
      !cached.websiteOffers?.length ||
      !cached.vendors?.length
    ) {
      // Refresh all caches if any missing
      handleRefreshProductOffers();
      handleRefreshBankOffers();
      handleRefreshWebsiteOffers();
      handleRefreshVendors();
    } else {
      setProductOffers(cached.productOffers);
      setBankOffers(cached.bankOffers);
      setWebsiteOffers(cached.websiteOffers);
      setVendors(cached.vendors);
    }
  }, []);

  const login = () => {
    isMobile ? signInWithRedirect(auth, provider) : signInWithPopup(auth, provider);
  };

  const logout = () => signOut(auth);

  const handleRefreshProductOffers = async () => {
    setLoadingProduct(true);
    try {
      const snap = await getDocs(collection(db, "product_offers"));

      let allOffers = [];
      snap.docs.forEach((doc) => {
        const data = doc.data();
        if (Array.isArray(data.offers)) {
          allOffers = allOffers.concat(data.offers);
        }
      });

      const now = new Date();
      // Get current date at 00:00:00 UTC (start of today in UTC)
      const utcTodayStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));

      const filteredOffers = allOffers.filter((offer) => {
        if (!offer.endDttm) return true;

        if (offer.endDttm.toDate) {
          const endDate = offer.endDttm.toDate();

          // Get endDate's UTC date at start of day (00:00:00 UTC)
          const utcEndDateStart = new Date(Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), endDate.getUTCDate()));

          // Include offers where endDate is today or in future (UTC)
          return utcEndDateStart >= utcTodayStart;
        }

        return true;
      });


      const cached = Cache.getData();
      Cache.setData({
        productOffers: filteredOffers,
        bankOffers: cached?.bankOffers || [],
        websiteOffers: cached?.websiteOffers || [],
        banksMeta: cached?.banksMeta || {},
        vendors: cached?.vendors || [],
      });

      setProductOffers(filteredOffers);
    } catch (err) {
      console.error("Failed to refresh product offers:", err);
    } finally {
      setLoadingProduct(false);
    }
  };

  const buildWhatsAppText = (offer) => {
    // Construct the dynamic URL
    const baseUrl = `https://www.dailydoseofdeal.com/product/${offer.id}`;
    const cleanUrl = `${baseUrl}?source=share`;

    // 🎯 Emoji map for offer types
    const emojiMap = {
      "Deal of the Day": "🎯",
      "Black Friday Deals": "🔥",
      "Clearance": "✨",
      "Lightning Deal": "⚡",
      "Limited Time Deal": "⏰",
      "Doorbusters": "💥",
    };

    // Case-insensitive emoji match
    const emojiKey = Object.keys(emojiMap).find(
      (key) => offer?.offerType?.toLowerCase() === key.toLowerCase()
    );
    const emoji = emojiKey ? emojiMap[emojiKey] : "🏷️";

    // ✅ Vendor text if available
    const vendorText = offer?.vendor ? ` (${offer.vendor})` : "";

    // ✅ Header formatting
    let message = "";
    if (offer?.offerType) {
      message += `${emoji} ${offer.offerType}${vendorText}\n`;
    } else {
      message += `Hey! ${emoji} Check out this new deal${vendorText}:\n`;
    }

    // ✅ Product name
    message += `*${offer.productName}*\n`;

    // ✅ Single-line deal price format
    const offerPrice = Number(offer.offerPrice || 0);
    const originalPrice = Number(offer.originalPrice || 0);
    let priceLine = "";

    if (offerPrice === 0) {
      priceLine = "💰 *Deal Price:* Free 💸";
    } else {
      priceLine = `💰 *Deal Price:* $${offerPrice}`;

      if (originalPrice > offerPrice) {
        const discount = Math.round(((originalPrice - offerPrice) / originalPrice) * 100);
        priceLine += `  ~$${originalPrice}~  (${discount}% OFF)`;
      }

      if (offer.cashback && offer.cashback > 0) {
        priceLine += ` After $${offer.cashback} Cashback`;
      }
    }

    message += priceLine + "\n";

    // ✅ Perks
    const perks = [];
    if (offer?.freeStorePickup === "Yes") perks.push("🏬 Free Store Pickup");
    if (offer?.freeShippingMinimum === 0) perks.push("🚚 Free Shipping");
    if (perks.length > 0) message += perks.join(" | ") + "\n";

    // ✅ Link
    message += `*Link:* ${cleanUrl}`;

    // ✅ Note
    if (offer.instructions?.trim()) {
      message += `\n\n📌 *Note:* Read the instructions for how to use the offer.`;
    }

    return message;
  };

  const handleGenerateWhatsAppText = async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const snap = await getDocs(collection(db, "product_offers"));
      let allOffers = [];

      snap.docs.forEach((doc) => {
        const data = doc.data();
        if (Array.isArray(data.offers)) {
          allOffers.push(
            ...data.offers.map((offer) => ({
              ...offer,
              vendor: offer.vendor || data.vendor || "", // keep vendor info
              id: `${doc.id}_${offer.offerId || Math.random().toString(36).slice(2, 8)}`,
              uploadedDttm: offer.uploadedDttm || data.uploadedDttm || null,
            }))
          );
        }
      });

      // filter today's offers
      let todaysOffers = allOffers.filter((o) => {
        if (!o.uploadedDttm) return false;
        const date = o.uploadedDttm.toDate
          ? o.uploadedDttm.toDate()
          : new Date(o.uploadedDttm);
        return date >= today;
      });

      if (vendorName.trim()) {
        // ✅ Case 1: Vendor entered → filter and sort newest first
        todaysOffers = todaysOffers.filter((o) =>
          o.vendor?.toLowerCase().includes(vendorName.toLowerCase())
        );

        todaysOffers.sort((a, b) => {
          const dateA = a.uploadedDttm?.toDate
            ? a.uploadedDttm.toDate()
            : new Date(a.uploadedDttm || 0);
          const dateB = b.uploadedDttm?.toDate
            ? b.uploadedDttm.toDate()
            : new Date(b.uploadedDttm || 0);
          return dateB - dateA; // newest → oldest
        });
      } else {
        // ✅ Case 2: No vendor → default discount-based sorting
        todaysOffers.sort((a, b) => {
          const offerPriceA = parseFloat(a.offerPrice);
          const offerPriceB = parseFloat(b.offerPrice);
          const originalPriceA = parseFloat(a.originalPrice);
          const originalPriceB = parseFloat(b.originalPrice);

          const discountA =
            !isNaN(offerPriceA) && offerPriceA === 0
              ? Infinity
              : !isNaN(originalPriceA) &&
                originalPriceA > 0 &&
                !isNaN(offerPriceA) &&
                offerPriceA > 0
                ? (originalPriceA - offerPriceA) / originalPriceA
                : null;

          const discountB =
            !isNaN(offerPriceB) && offerPriceB === 0
              ? Infinity
              : !isNaN(originalPriceB) &&
                originalPriceB > 0 &&
                !isNaN(offerPriceB) &&
                offerPriceB > 0
                ? (originalPriceB - offerPriceB) / originalPriceB
                : null;

          const dateA = a.uploadedDttm?.toDate
            ? a.uploadedDttm.toDate()
            : new Date(a.uploadedDttm || 0);
          const dateB = b.uploadedDttm?.toDate
            ? b.uploadedDttm.toDate()
            : new Date(b.uploadedDttm || 0);

          // discount sorting logic
          if (discountA !== null && discountB !== null) {
            if (discountB !== discountA) return discountB - discountA;
            return dateB - dateA;
          }
          if (discountA === null && discountB === null) {
            if ((offerPriceA || Infinity) !== (offerPriceB || Infinity)) {
              return (offerPriceA || Infinity) - (offerPriceB || Infinity);
            }
            return dateB - dateA;
          }
          if (discountA !== null && discountB === null) return -1;
          if (discountA === null && discountB !== null) return 1;
          return 0;
        });
      }

      const finalText = todaysOffers
        .map((o) => buildWhatsAppText(o))
        .join("\n\n-------------------\n\n");

      setWhatsAppText(finalText || "No deals found for today.");
    } catch (error) {
      console.error("Error generating WhatsApp text:", error);
      setWhatsAppText("Failed to generate WhatsApp text.");
    }
  };

  const handleDeleteMatchingOffers = async () => {
    const vendor = deleteVendor.trim();
    const offerType = deleteOfferType.trim();
    const productNameLines = deleteProductNames
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line);

    if (!vendor) {
      setDeleteResult("Vendor is required.");
      return;
    }

    if (!offerType && productNameLines.length === 0) {
      setDeleteResult("Please enter offer type or product names.");
      return;
    }

    setDeleting(true);
    try {
      const colRef = collection(db, "product_offers");
      const snapshot = await getDocs(colRef);

      let totalDeletedCount = 0;
      let updatedDocsCount = 0;

      for (const docSnap of snapshot.docs) {
        const docData = docSnap.data();
        const offers = docData.offers || [];

        // Identify offers to delete (but don't increment counters inside filter)
        const offersToDelete = offers.filter((offer) => {
          const vendorMatch = offer.vendor === vendor;
          const offerTypeMatch = offerType ? offer.offerType === offerType : false;
          const nameMatch = productNameLines.some((name) =>
            offer.productName?.toLowerCase() === name.toLowerCase()
          );
          return vendorMatch && (offerTypeMatch || nameMatch);
        });

        const filteredOffers = offers.filter(o => !offersToDelete.includes(o));

        if (filteredOffers.length !== offers.length) {
          await setDoc(doc(db, "product_offers", docSnap.id), {
            offers: filteredOffers,
            offerCount: filteredOffers.length,
          }, { merge: true });

          totalDeletedCount += offersToDelete.length;
          updatedDocsCount++;
        }
      }

      if (totalDeletedCount === 0) {
        setDeleteResult("No matching product offers found.");
      } else {
        setDeleteResult(`Deleted ${totalDeletedCount} offer(s) from ${updatedDocsCount} document(s).`);
      }
    } catch (err) {
      console.error("Error deleting offers:", err);
      setDeleteResult("Error deleting offers.");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteExpiredOffers = async () => {
    if (!deleteExpiredView) {
      setDeleteExpiredResult("Please select an offer type.");
      return;
    }
    if (!deleteExpiredDate) {
      setDeleteExpiredResult("Please select a date.");
      return;
    }

    setDeletingExpired(true);
    setDeleteExpiredResult("");

    try {
      // ✅ Build LOCAL cutoff date from YYYY-MM-DD (end of selected day)
      const [y, m, d] = deleteExpiredDate.split("-").map(Number);
      const selectedDate = new Date(y, m - 1, d, 23, 59, 59, 999);

      console.log("✅ Using cutoff:", selectedDate.toString(), selectedDate.toISOString());

      const collectionName =
        deleteExpiredView === "product" ? "product_offers" : "bank_offers";

      const colRef = collection(db, collectionName);
      const snapshot = await getDocs(colRef);

      let totalDeletedCount = 0;
      let updatedDocsCount = 0;

      for (const docSnap of snapshot.docs) {
        const docData = docSnap.data();
        const offers = docData.offers || [];

        const offersToDelete = offers.filter((offer) => {
          let expiryDate = null;

          if (deleteExpiredView === "product") {
            if (offer.endDttm?.toDate) expiryDate = offer.endDttm.toDate();
          } else {
            if (offer.expiryDttm?.toDate) expiryDate = offer.expiryDttm.toDate();
          }

          if (!expiryDate) return false;

          const isExpired = expiryDate.getTime() <= selectedDate.getTime();
 
          return isExpired;
        });

        if (offersToDelete.length === 0) continue;

        const filteredOffers = offers.filter(o => !offersToDelete.includes(o));

        if (filteredOffers.length === 0) {
          // delete doc if no offers left
          await deleteDoc(doc(db, collectionName, docSnap.id));
        } else {
          // update doc with remaining offers
          if (deleteExpiredView === "product") {
            await setDoc(
              doc(db, collectionName, docSnap.id),
              {
                offers: filteredOffers,
                offerCount: filteredOffers.length,
              },
              { merge: true }
            );
          } else {
            await setDoc(
              doc(db, collectionName, docSnap.id),
              { offers: filteredOffers },
              { merge: true }
            );
          }
        }

        totalDeletedCount += offersToDelete.length;
        updatedDocsCount++;
      }

      if (totalDeletedCount === 0) {
        setDeleteExpiredResult("No expired offers found to delete.");
      } else {
        setDeleteExpiredResult(
          `Deleted ${totalDeletedCount} expired offer(s) from ${updatedDocsCount} document(s).`
        );
      }
    } catch (err) {
      console.error("Error deleting expired offers:", err);
      setDeleteExpiredResult("Error deleting expired offers.");
    } finally {
      setDeletingExpired(false);
    }
  };



  const handleRefreshBankOffers = async () => {
    setLoadingBank(true);
    try {
      const snap = await getDocs(collection(db, "bank_offers"));
      const now = new Date();

      let allOffers = [];
      snap.docs.forEach((doc) => {
        const data = doc.data();
        if (Array.isArray(data.offers)) {
          allOffers = allOffers.concat(data.offers);
        }
      });

      const filteredOffers = allOffers.filter(
        (offer) => !offer.expiryDttm || (offer.expiryDttm.toDate ? offer.expiryDttm.toDate() > now : true)
      );

      const cached = Cache.getData();
      Cache.setData({
        productOffers: cached?.productOffers || [],
        bankOffers: filteredOffers,
        websiteOffers: cached?.websiteOffers || [],
        banksMeta: cached?.banksMeta || {},
        vendors: cached?.vendors || [],
      });

      setBankOffers(filteredOffers);
    } catch (err) {
      console.error("Failed to refresh bank offers:", err);
    } finally {
      setLoadingBank(false);
    }
  };


  const handleRefreshWebsiteOffers = async () => {
    setLoadingWebsite(true);
    try {
      const snap = await getDocs(collection(db, "website_offers"));
      const now = new Date();

      let allOffers = [];
      snap.docs.forEach((doc) => {
        const data = doc.data();
        if (Array.isArray(data.offers)) {
          allOffers = allOffers.concat(data.offers);
        }
      });

      const filteredOffers = allOffers.filter(
        (offer) => !offer.endDate || (offer.endDate.toDate ? offer.endDate.toDate() > now : true)
      );

      const cached = Cache.getData();
      Cache.setData({
        productOffers: cached?.productOffers || [],
        bankOffers: cached?.bankOffers || [],
        websiteOffers: filteredOffers,
        banksMeta: cached?.banksMeta || {},
        vendors: cached?.vendors || [],
      });

      setWebsiteOffers(filteredOffers);
    } catch (err) {
      console.error("Failed to refresh website offers:", err);
    } finally {
      setLoadingWebsite(false);
    }
  };


  // New: refresh vendors from Firestore collection "vendors"
  const handleRefreshVendors = async () => {
    setLoadingVendors(true);
    try {
      const snap = await getDocs(collection(db, "vendors"));
      const allVendors = [];

      snap.docs.forEach((doc) => {
        const data = doc.data();
        if (Array.isArray(data.vendors)) {
          data.vendors.forEach((vendor) => {
            if (!vendor.vendorName && vendor.id) {
              vendor.vendorName = vendor.id;
            }
            allVendors.push(vendor);
          });
        }
      });

      const cached = Cache.getData();
      Cache.setData({
        productOffers: cached?.productOffers || [],
        bankOffers: cached?.bankOffers || [],
        websiteOffers: cached?.websiteOffers || [],
        banksMeta: cached?.banksMeta || {},
        vendors: allVendors,
      });

      setVendors(allVendors);
    } catch (err) {
      console.error("Failed to refresh vendors:", err);
    } finally {
      setLoadingVendors(false);
    }
  };

  const getYesterdayDate = () => {
    const today = new Date();
    today.setDate(today.getDate() - 1); // yesterday
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };



  if (checking) return <p>Checking auth...</p>;

  if (!user) {
    return (
      <div className="p-6">
        <h2 className="text-xl font-semibold mb-4">Admin Login</h2>
        <button onClick={login} className="bg-blue-600 text-white px-4 py-2 rounded">
          Login with Google
        </button>
      </div>
    );
  }

  if (user.email !== ADMIN_EMAIL) {
    return (
      <div className="p-6">
        <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
        <p className="mb-4">You are not authorized to access the admin panel.</p>
        <button onClick={logout} className="bg-gray-600 text-white px-4 py-2 rounded">
          Logout
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-2">Welcome, Admin</h2>
      <p className="mb-4">
        {user.email}
        <button onClick={logout} className="ml-4 text-sm text-blue-600 underline">
          Logout
        </button>
      </p>

      {/* Admin view buttons */}
      <div className="flex gap-4 mb-6 flex-wrap">
        <button
          onClick={() => setView("product")}
          className={`px-4 py-2 rounded font-semibold ${view === "product" ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-800"
            }`}
        >
          🛍️ Add Product Offer
        </button>
        <button
          onClick={() => setView("bank")}
          className={`px-4 py-2 rounded font-semibold ${view === "bank" ? "bg-green-600 text-white" : "bg-gray-200 text-gray-800"
            }`}
        >
          🏦 Add Bank Offer
        </button>
        <button
          onClick={() => setView("weekly")}
          className={`px-4 py-2 rounded font-semibold ${view === "weekly" ? "bg-yellow-600 text-white" : "bg-gray-200 text-gray-800"
            }`}
        >
          📰 Add Weekly Ads
        </button>
        <button
          onClick={() => setView("website")}
          className={`px-4 py-2 rounded font-semibold ${view === "website" ? "bg-cyan-600 text-white" : "bg-gray-200 text-gray-800"
            }`}
        >
          🌐 Add Website Offer
        </button>
        <button
          onClick={() => setView("vendor")}
          className={`px-4 py-2 rounded font-semibold ${view === "vendor" ? "bg-pink-600 text-white" : "bg-gray-200 text-gray-800"
            }`}
        >
          🏪 Add Vendor
        </button>

        <button
          onClick={() => setView("special")}
          className={`px-4 py-2 rounded font-semibold ${view === "special"
            ? "bg-blue-800 text-white"
            : "bg-gray-200 text-gray-800"
            }`}
        >
          🎁 Add Special Offer
        </button>

        <button
          onClick={() => setView("cache")}
          className={`px-4 py-2 rounded font-semibold ${view === "cache" ? "bg-purple-600 text-white" : "bg-gray-200 text-gray-800"
            }`}
        >
          🔄 Refresh Cache
        </button>

        <button
          onClick={() => setView("updateOffer")}
          className={`px-4 py-2 rounded font-semibold ${view === "updateOffer" ? "bg-indigo-600 text-white" : "bg-gray-200 text-gray-800"
            }`}
        >
          ✏️ Update Product Offer
        </button>

        <button
          onClick={() => setView("deleteProduct")}
          className={`px-4 py-2 rounded font-semibold ${view === "deleteProduct" ? "bg-red-600 text-white" : "bg-gray-200 text-gray-800"
            }`}
        >
          🗑️ Delete Product Offers
        </button>

        <button
          onClick={() => setView("deleteExpired")}
          className={`px-4 py-2 rounded font-semibold ${view === "deleteExpired" ? "bg-red-700 text-white" : "bg-gray-200 text-gray-800"
            }`}
        >
          🗑️ Delete Expired Offers
        </button>

        <button
          onClick={() => setView("whatsapp")}
          className={`px-4 py-2 rounded font-semibold ${view === "whatsapp" ? "bg-green-700 text-white" : "bg-gray-200 text-gray-800"
            }`}
        >
          📱 WhatsApp Status
        </button>


      </div>

      {/* View logic */}
      {view === "product" && <AddOffer uploadedBy={user.email} />}
      {view === "updateOffer" && <UpdateOffer uploadedBy={user.email} />}
      {view === "bank" && <AddBankOffer uploadedBy={user.email} />}
      {view === "weekly" && <AddWeeklyAds />}
      {view === "website" && <AddWebsiteOffer />}
      {view === "vendor" && <AddVendor vendors={vendors} />}
      {view === "special" && <AddSpecialOffer uploadedBy={user.email} />}
      {view === "cache" && (
        <div className="space-y-6">
          <div className="space-y-2">
            <button
              onClick={handleRefreshProductOffers}
              disabled={loadingProduct}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              {loadingProduct ? "Refreshing Product Offers..." : "Refresh Product Offers"}
            </button>
            <p>Total Product Offers Cached: {productOffers.length}</p>
          </div>

          <div className="space-y-2">
            <button
              onClick={handleRefreshBankOffers}
              disabled={loadingBank}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              {loadingBank ? "Refreshing Bank Offers..." : "Refresh Bank Offers"}
            </button>
            <p>Total Bank Offers Cached: {bankOffers.length}</p>
          </div>

          <div className="space-y-2">
            <button
              onClick={handleRefreshWebsiteOffers}
              disabled={loadingWebsite}
              className="bg-cyan-600 text-white px-4 py-2 rounded hover:bg-cyan-700"
            >
              {loadingWebsite
                ? "Refreshing Website Offers..."
                : "Refresh Website Offers"}
            </button>
            <p>Total Website Offers Cached: {websiteOffers.length}</p>
          </div>
          <div className="space-y-2">
            <button
              onClick={handleRefreshVendors}
              disabled={loadingVendors}
              className="bg-pink-600 text-white px-4 py-2 rounded hover:bg-pink-700"
            >
              {loadingVendors ? "Refreshing Vendors..." : "Refresh Vendors"}
            </button>
            <p>Total Vendors Cached: {vendors.length}</p>
          </div>
        </div>
      )}
      {view === "deleteProduct" && (
        <div className="space-y-4 p-4 bg-red-50 border border-red-200 rounded">
          <h2 className="text-xl font-semibold text-red-800">🗑️ Delete Product Offers</h2>

          <div>
            <label className="block font-medium mb-1">Vendor (required):</label>
            <input
              type="text"
              value={deleteVendor}
              onChange={(e) => setDeleteVendor(e.target.value)}
              placeholder="e.g. walmart"
              className="w-full border px-2 py-1 rounded"
            />
          </div>

          <div>
            <label className="block font-medium mb-1">Offer Type (optional):</label>
            <input
              type="text"
              value={deleteOfferType}
              onChange={(e) => setDeleteOfferType(e.target.value)}
              placeholder="e.g. instant"
              className="w-full border px-2 py-1 rounded"
            />
          </div>

          <div>
            <label className="block font-medium mb-1">
              Product Names (optional – one per line):
            </label>
            <textarea
              rows="5"
              value={deleteProductNames}
              onChange={(e) => setDeleteProductNames(e.target.value)}
              placeholder="e.g.\nNike Shoes\nSamsung TV"
              className="w-full border px-2 py-1 rounded"
            />
          </div>

          <button
            onClick={handleDeleteMatchingOffers}
            disabled={deleting}
            className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
          >
            {deleting ? "Deleting..." : "Delete Offers"}
          </button>

          {deleteResult && (
            <p className="text-sm text-red-700 mt-2">{deleteResult}</p>
          )}

        </div>
      )}
      {view === "deleteExpired" && (
        <div className="space-y-4 p-4 bg-red-50 border border-red-300 rounded max-w-md">
          <h2 className="text-xl font-semibold text-red-800 mb-4">🗑️ Delete Expired Offers</h2>

          <div>
            <label className="block font-medium mb-1">Select Offer Type (required):</label>
            <select
              value={deleteExpiredView}
              onChange={(e) => setDeleteExpiredView(e.target.value)}
              className="w-full border px-2 py-1 rounded"
            >
              <option value="">-- Select --</option>
              <option value="product">Product Offer</option>
              <option value="bank">Bank Offer</option>
            </select>
          </div>

          <div>
            <label className="block font-medium mb-1">Expired On or Before (required):</label>
            <input
              type="date"
              value={deleteExpiredDate}
              onChange={(e) => setDeleteExpiredDate(e.target.value)}
              className="w-full border px-2 py-1 rounded"
              max={getYesterdayDate()}
            />
          </div>

          <button
            onClick={handleDeleteExpiredOffers}
            disabled={deletingExpired}
            className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
          >
            {deletingExpired ? "Deleting..." : "Delete Expired Offers"}
          </button>

          {deleteExpiredResult && (
            <p className="text-sm text-red-700 mt-2 whitespace-pre-wrap">{deleteExpiredResult}</p>
          )}
        </div>
      )}
      {view === "whatsapp" && (
        <div className="p-4 bg-green-50 border border-green-200 rounded space-y-4 max-w-2xl">
          <h2 className="text-xl font-semibold text-green-800">📱 WhatsApp Status Messages</h2>

          <div className="flex gap-2 mb-2">
            <input
              type="text"
              placeholder="Enter Vendor Name"
              value={vendorName}
              onChange={(e) => setVendorName(e.target.value)}
              className="border px-2 py-1 rounded"
            />

            <button
              onClick={handleGenerateWhatsAppText}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              Generate Messages for Today
            </button>

            {whatsAppText && (
              <button
                onClick={() => navigator.clipboard.writeText(whatsAppText)}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
              >
                Copy All
              </button>
            )}
          </div>


          <textarea
            rows={20}
            value={whatsAppText}
            readOnly
            className="w-full border px-2 py-1 rounded mt-2"
          />

        </div>
      )}
    </div>
  );
}
